import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { formatINR } from "../mock";
import { useToast } from "../hooks/use-toast";

export default function BookingModal({ open, onOpenChange, tour }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    travelers: 2,
    date: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const total = tour.price * Number(form.travelers || 1);

  // Pre-fill name/email from the logged-in user whenever the modal opens.
  useEffect(() => {
    if (open && user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
      }));
    }
  }, [open, user]);

  const submit = async () => {
    if (!user) {
      toast({ title: "Please log in", description: "You need an account to book a trip." });
      onOpenChange(false);
      navigate("/login");
      return;
    }
    if (!form.name || !form.email || !form.phone || !form.date) {
      toast({ title: "Missing details", description: "Please fill all fields.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        tourId: tour.id,
        tourTitle: tour.title,
        tourLocation: tour.location,
        price: tour.price,
        travelers: Number(form.travelers),
        total,
        name: form.name,
        email: form.email,
        phone: form.phone,
        date: form.date,
      });
      setDone(true);
    } catch (e) {
      toast({ title: "Booking failed", description: e.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setDone(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setDone(false); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-md">
        {done ? (
          <div className="text-center py-4">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
            <h3 className="mt-4 text-xl font-bold text-slate-900">Booking Confirmed!</h3>
            <p className="mt-2 text-sm text-slate-500">
              Your seat for <b>{tour.title}</b> is reserved. Status: <b>Pending Payment</b>. Our team will reach out shortly.
            </p>
            <div className="mt-5 flex gap-3">
              <Button variant="outline" className="flex-1" onClick={close}>Close</Button>
              <Button className="flex-1 bg-coral-600 text-white" onClick={() => { close(); navigate("/my-trips"); }}>My Trips</Button>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">Book {tour.title}</DialogTitle>
              <DialogDescription>{tour.duration} • {formatINR(tour.price)} / person</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div><Label>Full Name</Label><Input value={form.name} onChange={set("name")} placeholder="Your name" className="mt-1" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={set("email")} type="email" placeholder="you@email.com" className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} placeholder="+91..." className="mt-1" /></div>
                <div><Label>Travelers</Label><Input value={form.travelers} onChange={set("travelers")} type="number" min={1} className="mt-1" /></div>
              </div>
              <div><Label>Preferred Date</Label><Input value={form.date} onChange={set("date")} type="date" className="mt-1" /></div>
              <div className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-3">
                <span className="text-sm text-slate-600">Estimated total</span>
                <span className="font-display font-bold text-slate-900">{formatINR(total)}</span>
              </div>
              <Button onClick={submit} disabled={submitting} className="w-full h-11 bg-coral-600 hover:bg-coral-700 text-white">{submitting ? "Booking..." : "Confirm Booking"}</Button>
              <p className="text-center text-xs text-slate-400">Payment via Razorpay coming soon — booking held as Pending Payment.</p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
